import { useId, type ReactNode } from "react";
import { StepLoading } from "@/base/wizard/StepLoading";
import { ExtractedField } from "@/components/ExtractedReview";
import { usePriorCarriers } from "@/lib/usePriorCarriers";
import { NumericField } from "@/components/NumericField";
import { StepLoadProblem } from "@/components/submissions/wc/StepLoadProblem";
import {
  HiddenAnswerErrors,
  QuestionField,
} from "@/components/submissions/wc/QuestionField";
import { ElLimitsField } from "@/components/submissions/wc/ElLimitsField";
import { QUESTION_COLUMNS } from "@/components/submissions/wc/grids";
import { TOUCH_TARGET } from "@/components/buttonStyles";
import { FIELD_ERROR, FIELD_HELP } from "@/components/fieldStyles";
import { SectionCard, YesNo, type WizardStepContext } from "@/base";
import type { ManifestField } from "@/lib/brokerProducts";
import type { WcDraft } from "@/lib/submissions/wc/draft";
import {
  CONTRADICTION,
  DECLINED,
  declinedContradicts,
  EL_LIMIT_KEYS,
  elLimitsAsked,
  isOtherLimit,
} from "@/lib/submissions/wc/coverage";
import {
  answersWithDefaults,
  asBoolean,
  asNumber,
  CONFIRMATION,
  COVERAGE,
  GENERAL_INFORMATION,
  isAsked,
  questionCells,
  questionSections,
  QUESTIONS_UNAVAILABLE,
  SUBCONTRACTED_PERCENT,
  SUBCONTRACTORS_USED,
  shownAnswers,
  type QuestionCell,
  type QuestionSection,
  type WcAnswer,
  type WcAnswers,
} from "@/lib/submissions/wc/questions";
import { useWcManifest } from "@/lib/submissions/wc/useWcManifest";

// The WC wizard's Questions and coverage step (BRK-S304). The ACORD 130
// questions, their wording and their order are the manifest's (PMS-S703):
// each is a Yes/No that can stay unanswered, with the form's text as its help.
// The coverage values are asked, never defaulted: a manifest default shows
// only as a hint. An uploaded ACORD 130's answers fill them, each marked until
// changed (BRK-S306). From md the section is two columns, a follow-up in its
// question's cell (BRK-S345), in a white section card, as the account form's
// groups are (BRK-S349). Each section is its own sub-step (BRK-S387), Coverage
// then General information, then Schedule rating, so the card takes its title
// from the page. BRK-S392 (PMS-S811, PMS-S812):
// - Coverage asks the employers' liability limits as one set (`ElLimitsField`)
//   when the manifest publishes them, in one cell like any other select once
//   the three amounts were no longer shown under it (BRK-S508), and flags a
//   declined No that contradicts a lapse on Characteristics & History.
// - General information starts each ACORD question at PMS's default, shown
//   chosen, and ends with the broker's confirmation, which any changed
//   answer clears.
// - Schedule rating's fields are drawn by type, as Coverage's are.

export function QuestionsStep({
  section,
  draft,
  setDraft,
  setLoaded,
  errors,
  busy,
  extracted,
}: WizardStepContext<WcDraft> & { section: QuestionSection }) {
  const { problem, retry } = useWcManifest(draft, setLoaded);
  const carriers = usePriorCarriers(
    Boolean(
      draft.manifest?.fields.some(
        (f) =>
          f.section === section &&
          f.type === "priorCarrier" &&
          isAsked(f, draft.answers),
      ),
    ),
  );

  if (problem) {
    return <StepLoadProblem problem={problem} onRetry={retry} />;
  }
  if (draft.manifest === null) {
    return <StepLoading label="Loading the questions…" />;
  }
  const sections = questionSections(draft.manifest);
  if (!sections) {
    // Never an empty step with a live Continue (BRK-S114): Next is refused
    // with the same sentence.
    return <p className="text-body text-brk-dim">{QUESTIONS_UNAVAILABLE}</p>;
  }

  // General information's questions read as their defaults until answered.
  const answers = answersWithDefaults(draft.manifest, draft.answers);
  // What is asked now, a follow-up hidden with its question.
  const shown = shownAnswers(draft.manifest.fields, answers);
  /** The broker's answer: the document's mark goes from each key it sets.
   *  On General information, any answer but the confirmation clears it. */
  const set = (patch: Record<string, WcAnswer>) => {
    for (const key of Object.keys(patch)) extracted.touch(key);
    const unconfirm =
      section === GENERAL_INFORMATION && !(CONFIRMATION in patch)
        ? { [CONFIRMATION]: undefined }
        : {};
    setDraft((d) => ({
      ...d,
      answers: { ...d.answers, ...patch, ...unconfirm },
    }));
  };

  /** One ACORD question, or the % that qualifies question 6's Yes. */
  const general = (f: ManifestField) => (
    <ExtractedField marked={extracted.isPending(f.key)}>
      {f.key === SUBCONTRACTED_PERCENT ? (
        <NumericField
          kind="percent"
          label={f.label}
          value={asNumber(answers[f.key])}
          onChange={(value) => set({ [f.key]: value })}
          error={errors[f.key]}
          min={f.min}
          max={f.max}
          required
        />
      ) : (
        <YesNo
          label={f.label}
          help={f.helpText}
          value={asBoolean(answers[f.key])}
          onChange={(yes) =>
            // A No to subcontractors clears the % it qualified.
            set(
              f.key === SUBCONTRACTORS_USED && !yes
                ? { [f.key]: yes, [SUBCONTRACTED_PERCENT]: undefined }
                : { [f.key]: yes },
            )
          }
          error={errors[f.key]}
          disabled={busy}
        />
      )}
    </ExtractedField>
  );
  const coverage = (f: ManifestField) => (
    <ExtractedField marked={extracted.isPending(f.key)}>
      <QuestionField
        field={f}
        value={answers[f.key]}
        onChange={(value) => set({ [f.key]: value })}
        errors={errors}
        busy={busy}
        carriers={carriers}
      />
      <HiddenAnswerErrors
        question={f}
        fields={draft.manifest?.fields ?? []}
        answers={shown}
        errors={errors}
      />
      {f.key === DECLINED && declinedContradicts(draft.manifest, answers) && (
        <p role="status" className="text-sm text-brk-text">
          {CONTRADICTION}
        </p>
      )}
    </ExtractedField>
  );

  const fields =
    section === COVERAGE
      ? sections.coverage
      : section === GENERAL_INFORMATION
        ? sections.general
        : sections.schedule;
  if (fields.length === 0) {
    return (
      <p className="text-body text-brk-dim">
        Nothing to answer here for this application.
      </p>
    );
  }

  // The limits as one choice, in the first limit's place.
  const oneLimit = section === COVERAGE && elLimitsAsked(draft.manifest);
  const limits = (
    <ExtractedField marked={EL_LIMIT_KEYS.some((k) => extracted.isPending(k))}>
      <ElLimitsField
        combinations={draft.manifest.elLimitCombinations ?? []}
        answers={answers}
        onChange={set}
        errors={errors}
        busy={busy}
      />
    </ExtractedField>
  );
  const render = (f: ManifestField) =>
    oneLimit && f.key === EL_LIMIT_KEYS[0]
      ? limits
      : section === GENERAL_INFORMATION
        ? general(f)
        : coverage(f);
  const asked = fields.filter(
    (f) => f.key !== CONFIRMATION && !(oneLimit && isOtherLimit(f.key)),
  );
  const confirmation = fields.find((f) => f.key === CONFIRMATION);
  return (
    <Group
      cells={questionCells(asked)}
      answers={shown}
      render={render}
      footer={
        confirmation && (
          <Confirmation
            label={confirmation.label}
            checked={answers[CONFIRMATION] === true}
            onChange={(yes) => set({ [CONFIRMATION]: yes || undefined })}
            error={errors[CONFIRMATION]}
            disabled={busy}
          />
        )
      }
    />
  );
}

/** General information's last word (PMS-S812): the usual answers were
 *  chosen for the broker, who confirms them all. A checkbox, since only a
 *  Yes is an answer. */
function Confirmation({
  label,
  checked,
  onChange,
  error,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
  disabled: boolean;
}) {
  const errorId = useId();
  return (
    <div className="mt-5 flex flex-col gap-2 border-t border-brk-border pt-4">
      <p className={FIELD_HELP}>
        We&apos;ve pre-selected the usual answers. Check each one.
      </p>
      <label
        className={`flex items-center gap-2 text-sm font-medium text-brk-text ${TOUCH_TARGET}`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          required
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        {label}
      </label>
      {error && (
        <p id={errorId} className={FIELD_ERROR}>
          {error}
        </p>
      )}
    </div>
  );
}

function Group({
  cells,
  answers,
  render,
  footer,
}: {
  cells: QuestionCell[];
  answers: WcAnswers;
  render: (field: ManifestField) => ReactNode;
  /** What closes the card, under the grid. */
  footer?: ReactNode;
}) {
  return (
    <SectionCard>
      <div className={QUESTION_COLUMNS}>
        {cells
          .filter(({ field }) => isAsked(field, answers))
          .map(({ field, followUp }) => (
            <div
              key={field.key}
              data-question={field.key}
              className="flex min-w-0 flex-col gap-3"
            >
              {render(field)}
              {followUp && isAsked(followUp, answers) && render(followUp)}
            </div>
          ))}
      </div>
      {footer}
    </SectionCard>
  );
}
